import type {
  BracketEntry,
  BracketGroupPick,
  BracketKoPick,
  Fixture,
  League,
  Prediction,
  User,
} from '@punditxi/db';

export type FixtureView = Fixture & {
  homeTeamName: string | null;
  homeTeamShortName: string | null;
  homeTeamCrest: string | null;
  awayTeamName: string | null;
  awayTeamShortName: string | null;
  awayTeamCrest: string | null;
  competitionName: string | null;
  competitionShortName: string | null;
  predictionWindowStatus: 'OPEN' | 'CLOSING_SOON' | 'LOCKED';
};
import { storage } from './telegram';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://api.punditxi.app';
const JWT_KEY = 'jwt';

let cachedJwt: string | null = null;

export async function getJwt(): Promise<string | null> {
  if (cachedJwt) return cachedJwt;
  cachedJwt = await storage.get(JWT_KEY);
  return cachedJwt;
}

export async function setJwt(token: string): Promise<void> {
  cachedJwt = token;
  await storage.set(JWT_KEY, token);
}

export async function clearJwt(): Promise<void> {
  cachedJwt = null;
  await storage.remove(JWT_KEY);
}

export class ApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly body?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}, _retried = false): Promise<T> {
  const jwt = await getJwt();
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (jwt) headers.authorization = `Bearer ${jwt}`;

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });

  // Stale-JWT recovery: if a known-authenticated call 401s and we haven't
  // already retried, drop the cached JWT, re-auth via initData, and retry
  // exactly once. This handles JWT_SECRET rotation on the server.
  if (res.status === 401 && !_retried && path !== '/api/auth/init' && path !== '/api/auth/dev') {
    await clearJwt();
    const tg = (window as unknown as { Telegram?: { WebApp?: { initData: string } } }).Telegram
      ?.WebApp;
    if (tg?.initData) {
      try {
        const reauth = (await request(
          '/api/auth/init',
          { method: 'POST', body: JSON.stringify({ initData: tg.initData }) },
          true,
        )) as { token: string };
        await setJwt(reauth.token);
        return request(path, init, true);
      } catch {
        // fall through to the regular error path below
      }
    }
  }

  const text = await res.text();
  const json = text ? (() => { try { return JSON.parse(text); } catch { return null; } })() : null;

  if (!res.ok) {
    throw new ApiError(
      typeof json === 'object' && json && 'error' in json ? String(json.error) : `HTTP ${res.status}`,
      res.status,
      json,
    );
  }
  return json as T;
}

export const api = {
  // Auth
  authInit(initData: string): Promise<{ token: string; user: User }> {
    return request('/api/auth/init', { method: 'POST', body: JSON.stringify({ initData }) });
  },
  authDev(telegramId: number, firstName: string): Promise<{ token: string; user: User }> {
    return request('/api/auth/dev', { method: 'POST', body: JSON.stringify({ telegramId, firstName }) });
  },
  me(): Promise<{ user: User }> {
    return request('/api/me');
  },

  // Fixtures
  fixturesToday(): Promise<{ fixtures: FixtureView[] }> {
    return request('/api/fixtures/today');
  },
  fixturesUpcoming(days = 7): Promise<{ fixtures: FixtureView[] }> {
    return request(`/api/fixtures/upcoming?days=${days}`);
  },
  fixtureById(id: number): Promise<{ fixture: FixtureView }> {
    return request(`/api/fixtures/${id}`);
  },
  fixtureMyPrediction(id: number): Promise<{ prediction: Prediction | null }> {
    return request(`/api/fixtures/${id}/my-prediction`);
  },

  // Predictions
  predictionsSubmit(body: {
    fixtureId: number;
    pick1x2?: 'H' | 'D' | 'A' | null;
    pickBtts?: boolean | null;
    pickHomeScore?: number | null;
    pickAwayScore?: number | null;
    pickWinnerTeamId?: number | null;
    pickMethod?: 'AFTER_90' | 'AFTER_ET' | 'ON_PENS' | null;
    pickGoalscorerPlayerId?: number | null;
    boostApplied?: boolean;
    insuranceApplied?: boolean;
  }): Promise<{ predictionId: number; updated: boolean }> {
    return request('/api/predictions', { method: 'POST', body: JSON.stringify(body) });
  },
  predictionsMe(limit = 50, offset = 0): Promise<{ predictions: Prediction[] }> {
    return request(`/api/predictions/me?limit=${limit}&offset=${offset}`);
  },

  // Bracket
  bracketMe(): Promise<{
    entry: BracketEntry | null;
    groupPicks: BracketGroupPick[];
    koPicks: BracketKoPick[];
  }> {
    return request('/api/bracket/me');
  },
  bracketSubmitPicks(body: {
    groupPicks?: { groupLabel: string; position: 1 | 2 | 3; teamId: number }[];
    koPicks?: { stage: 'R32' | 'R16' | 'QF' | 'SF' | 'FINAL'; slotId: string; teamId: number | null }[];
    championTeamId?: number | null;
    goldenBootPlayerId?: number | null;
  }): Promise<{ ok: true; updatedAt: number }> {
    return request('/api/bracket/picks', { method: 'POST', body: JSON.stringify(body) });
  },
  bracketLeaderboard(): Promise<{
    leaderboard: { userId: number; firstName: string; telegramUsername: string | null; bracketPoints: number }[];
  }> {
    return request('/api/bracket/leaderboard');
  },
  bracketReLock(): Promise<{ ok: boolean; freeReLocksRemaining?: number; paid?: boolean; needsPurchase?: boolean }> {
    return request('/api/bracket/relock', { method: 'POST' });
  },

  // WC
  wcGroups(): Promise<{
    groups: {
      groupLabel: string;
      teams: {
        teamId: number;
        groupLabel: string;
        name: string;
        shortName: string | null;
        crestUrl: string | null;
        country: string | null;
        fifaRanking: number | null;
      }[];
    }[];
  }> {
    return request('/api/wc/groups');
  },
  wcTopScorers(): Promise<{ topScorers: unknown[] }> {
    return request('/api/wc/top-scorers');
  },
  wcPlayerSearch(q: string): Promise<{
    players: { id: number; name: string; teamId: number | null; teamName: string | null }[];
    pending?: boolean;
  }> {
    return request(`/api/wc/players/search?q=${encodeURIComponent(q)}`);
  },

  // Leagues
  leaguesPublic(): Promise<{ leagues: League[] }> {
    return request('/api/leagues/public');
  },
  leaguesMe(): Promise<{
    leagues: { league: League; member: { totalPoints: number; currentRank: number | null } }[];
  }> {
    return request('/api/leagues/me');
  },
  leagueDetail(slug: string): Promise<{
    league: League;
    leaderboard: {
      userId: number;
      totalPoints: number;
      bracketPoints: number;
      firstName: string;
      telegramUsername: string | null;
    }[];
  }> {
    return request(`/api/leagues/${slug}`);
  },
  createLeague(body: { slug: string; name: string; description?: string }): Promise<{ league: League }> {
    return request('/api/leagues', { method: 'POST', body: JSON.stringify(body) });
  },
  joinLeague(slug: string): Promise<{ joined: true; alreadyMember: boolean }> {
    return request(`/api/leagues/${slug}/join`, { method: 'POST' });
  },
  sponsorLeague(
    slug: string,
    body: { name: string; logoUrl?: string; linkUrl?: string },
  ): Promise<{ ok: true }> {
    return request(`/api/leagues/${slug}/sponsor`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  markOnboarded(): Promise<{ ok: true }> {
    return request('/api/profile/onboarded', { method: 'POST' });
  },
  accuracyTrend(): Promise<{
    weeks: { weekStart: number; picked: number; correct: number; accuracyPct: number }[];
  }> {
    return request('/api/profile/accuracy-trend');
  },

  // Dev grants — gated server-side by DEV_SHORTCUT_ENABLED. Calls 403 when off.
  devMe(): Promise<{
    userId: number;
    starsCredit: number;
    premiumUntil: number | null;
    premiumActive: boolean;
    hasBracketPremium: boolean;
  }> {
    return request('/dev/me');
  },
  devGrantPremium(days = 30): Promise<{ ok: true; premiumUntil: number; daysGranted: number }> {
    return request('/dev/grant-premium', { method: 'POST', body: JSON.stringify({ days }) });
  },
  devRevokePremium(): Promise<{ ok: true }> {
    return request('/dev/revoke-premium', { method: 'POST' });
  },
  devGrantCredit(amount = 200): Promise<{ ok: true; starsCredit: number; granted: number }> {
    return request('/dev/grant-credit', { method: 'POST', body: JSON.stringify({ amount }) });
  },

  // Admin — gated server-side by telegram_id allowlist on api worker.
  adminMetrics(): Promise<{
    metrics: {
      total_users: number;
      new_today: number;
      dau_24h: number;
      bracket_holders: number;
      premium_subscribers: number;
      preds_24h: number;
      preds_today: number;
      stars_24h: number;
      stars_today: number;
      fixtures_today: number;
      scored_24h: number;
    };
    fetchedAt: number;
  }> {
    return request('/api/admin/metrics');
  },
  adminBroadcast(body: {
    text: string;
    kind?: string;
    bucketKey?: string;
    miniAppButton?: { text: string; url: string };
  }): Promise<{
    ok: boolean;
    started?: boolean;
    audienceSize?: number;
    kind?: string;
    bucketKey?: string;
    error?: string;
  }> {
    return request('/api/admin/broadcast', { method: 'POST', body: JSON.stringify(body) });
  },

  // Profile stats
  profileStats(): Promise<{
    totalScored: number;
    markets: {
      oneXTwo: { picked: number; correct: number; accuracyPct: number | null };
      btts: { picked: number; correct: number; accuracyPct: number | null };
      correctScore: { picked: number; correct: number; accuracyPct: number | null };
      knockoutWinner: { picked: number; correct: number; accuracyPct: number | null };
    };
    perCompetition: {
      id: number;
      name: string;
      shortName: string;
      picks: number;
      correct: number;
      points: number;
      accuracyPct: number | null;
    }[];
  }> {
    return request('/api/profile/stats');
  },

  // Leaderboard
  leaderboardGlobal(period: 'week' | 'season' | 'all' = 'all'): Promise<{
    leaderboard: {
      id: number;
      firstName: string;
      telegramUsername: string | null;
      totalPoints: number;
      bracketPoints: number;
    }[];
  }> {
    return request(`/api/leaderboard/global?period=${period}`);
  },

  // Purchases
  initiatePurchase(
    productCode: string,
    opts?: { fixtureId?: number; leagueSlug?: string },
  ): Promise<
    | { invoiceLink: string; stars: number; paid: false }
    | { paid: true; viaCredit: true; stars: number; creditRemaining: number }
  > {
    return request('/api/purchases/initiate', {
      method: 'POST',
      body: JSON.stringify({ productCode, ...(opts ?? {}) }),
    });
  },
  myCredit(): Promise<{ availableCreditStars: number }> {
    return request('/api/purchases/credit');
  },
  mySubscription(): Promise<{
    isActive: boolean;
    premiumUntil: number | null;
    secondsRemaining: number;
  }> {
    return request('/api/purchases/subscription');
  },
};
